//@flow
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import VehicleModelImportHelper from './VehicleModelImportHelper';

const VEHICLEMODEL_IMPORT_FORMAT: Array<TableColumnTypes> = [
    {
        titleKey: 'masterVehicleModelProperties.id',
        id: 'id',
        disabled: true,
        fixed: true,
        required: true,
        templateValue: 'masterImportTemplateValues.masterVehicleModel.id'
    },
    {
        titleKey: 'masterVehicleModelProperties.code',
        id: 'code',
        disabled: true,
        templateValue: 'masterImportTemplateValues.masterVehicleModel.code'
    },
    {
        titleKey: 'masterVehicleModelProperties.name',
        id: 'name',
        required: true,
        templateValue: 'masterImportTemplateValues.masterVehicleModel.name'
    },
    {
        titleKey: 'masterVehicleModelProperties.description',
        id: 'description',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.description'
    },
    {
        titleKey: 'masterVehicleModelProperties.vehicleType',
        id: 'vehicleType',
        required: true,
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.vehicleType',
        processCell: VehicleModelImportHelper.processUpperCaseId,
        validateCell: VehicleModelImportHelper.validateVehicleType
    },
    {
        titleKey: 'masterVehicleModelProperties.transporterId',
        id: 'transporterId',
        accessor: ['transporterId', 'transporterId'],
        required: true,
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.transporterId',
        serializeCell: VehicleModelImportHelper.serializeTransporterId,
        getModelFieldAccessor:
            VehicleModelImportHelper.getTransporterIdAccessor,
        validateCellAsync: ({
            entity,
            header,
            onSuccess,
            onFailure,
            validateTransporterIds,
            clientId,
            t
        }) => {
            VehicleModelImportHelper.validateEntityIdAsync({
                validator: validateTransporterIds,
                entityNameId: 'masterVehicleModel',
                clientId,
                onSuccess,
                onFailure,
                entity,
                header,
                t
            });
        }
    },
    {
        titleKey: 'masterVehicleModelProperties.skillId',
        id: 'skills',
        templateValue: 'masterImportTemplateValues.masterVehicleModel.skills',
        processCell: VehicleModelImportHelper.processSkills,
        validateCell: VehicleModelImportHelper.validateSkills,
        serializeCell: VehicleModelImportHelper.serializeSkills
    },
    {
        titleKey: 'masterVehicleModelProperties.allowMultiTrip',
        id: 'allowMultiTrip',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.booleanValue',
        validateCell: VehicleModelImportHelper.getBooleanValidator(),
        processCell: VehicleModelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterVehicleModelProperties.limitMultiTrip',
        id: 'limitMultiTrip',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.booleanValue',
        validateCell: VehicleModelImportHelper.validateLimitMultiTrip,
        processCell: VehicleModelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterVehicleModelProperties.maxAdditionalTrips',
        id: 'maxAdditionalTrips',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxAdditionalTrips',
        validateCell: VehicleModelImportHelper.validateMaxAdditionalTrips
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.loadingDuration',
        id: 'loadingDuration',
        accessor: ['loadingParams', 'loadingDuration'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.loadingParams.loadingDuration',
        processCell: VehicleModelImportHelper.processMinutesToSeconds
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.unloadingDuration',
        id: 'unloadingDuration',
        accessor: ['loadingParams', 'unloadingDuration'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.loadingParams.unloadingDuration',
        processCell: VehicleModelImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.transactionTimePerTask',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes')
        }),
        id: 'transactionTimePerTask',
        accessor: ['limits', 'transactionTimePerTask'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.transactionTimePerTask',
        processCell: VehicleModelImportHelper.processMinTransactionTime
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.km'),
            limit: t('masterVehicleModelProperties.limits.distance')
        }),
        id: 'minDistance',
        accessor: ['limits', 'distance', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.minDistance',
        processCell: VehicleModelImportHelper.processKiloMetersToMeters
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.km'),
            limit: t('masterVehicleModelProperties.limits.distance')
        }),
        id: 'maxDistance',
        accessor: ['limits', 'distance', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxDistance',
        validateCell: VehicleModelImportHelper.validateHigherThresh,
        processCell: VehicleModelImportHelper.processKiloMetersToMeters
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.kg'),
            limit: t('masterVehicleModelProperties.limits.weight')
        }),
        id: 'minWeight',
        accessor: ['limits', 'weight', 'lowerThresh'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.minWeight'
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.kg'),
            limit: t('masterVehicleModelProperties.limits.weight')
        }),
        id: 'maxWeight',
        accessor: ['limits', 'weight', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxWeight',
        validateCell: VehicleModelImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.travelTime')
        }),
        id: 'minTravelTime',
        accessor: ['limits', 'travelTime', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.minTravelTime',
        processCell: VehicleModelImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.travelTime')
        }),
        id: 'maxTravelTime',
        accessor: ['limits', 'travelTime', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxTravelTime',
        validateCell: VehicleModelImportHelper.validateHigherThresh,
        processCell: VehicleModelImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.totalTime')
        }),
        id: 'minTotalTime',
        accessor: ['limits', 'totalTime', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.minTotalTime',
        processCell: VehicleModelImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.minutes'),
            limit: t('masterVehicleModelProperties.limits.totalTime')
        }),
        id: 'maxTotalTime',
        accessor: ['limits', 'totalTime', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxTotalTime',
        validateCell: VehicleModelImportHelper.validateHigherThresh,
        processCell: VehicleModelImportHelper.processMinutesToSeconds
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.location')
        }),
        id: 'minLocation',
        accessor: ['limits', 'location', 'lowerThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.minLocation'
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.location')
        }),
        id: 'maxLocation',
        accessor: ['limits', 'location', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxLocation',
        validateCell: VehicleModelImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.minAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.task')
        }),
        id: 'minTask',
        accessor: ['limits', 'task', 'lowerThresh'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.minTask'
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithUnits.maxAllowedLimit',
        translationProps: ({ t }) => ({
            unit: t('translations:units.count'),
            limit: t('masterVehicleModelProperties.limits.task')
        }),
        id: 'maxTask',
        accessor: ['limits', 'task', 'higherThresh'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.maxTask',
        validateCell: VehicleModelImportHelper.validateHigherThresh
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithoutUnits.minAllowedLimit',
        translationProps: ({ t }) => {
            return {
                limit: t('masterVehicleModelProperties.limits.volume')
            };
        },
        id: 'minVolume',
        accessor: ['limits', 'volume', 'lowerThresh'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.minVolume'
    },
    {
        titleKey:
            'masterVehicleModelProperties.limitsWithoutUnits.maxAllowedLimit',
        translationProps: ({ t }) => {
            return {
                limit: t('masterVehicleModelProperties.limits.volume')
            };
        },
        id: 'maxVolume',
        accessor: ['limits', 'volume', 'higherThresh'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxVolume',
        validateCell: VehicleModelImportHelper.validateHigherThresh
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.unladenWeight',
        id: 'unladenWeight',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.unladenWeight'
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.maxSpeed',
        id: 'maxSpeed',
        accessor: ['travelParams', 'maxSpeed'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.travelParams.maxSpeed'
    },
    {
        titleKey: 'masterVehicleModelProperties.isColdChain',
        id: 'isColdChain',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.booleanValue',
        validateCell: VehicleModelImportHelper.getBooleanValidator(),
        processCell: VehicleModelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterVehicleModelProperties.isDeepFreeze',
        id: 'isDeepFreeze',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.booleanValue',
        validateCell: VehicleModelImportHelper.getBooleanValidator(),
        processCell: VehicleModelImportHelper.getBooleanProcessor()
    },
    {
        titleKey: 'masterVehicleModelProperties.fuelType',
        id: 'fuelType',
        required: true,
        templateValue: 'masterImportTemplateValues.masterVehicleModel.fuelType',
        processCell: VehicleModelImportHelper.processUpperCaseId,
        validateCell: VehicleModelImportHelper.validateFuelType
    },
    {
        titleKey: 'masterVehicleModelProperties.fleetType',
        id: 'fleetType',
        required: true,
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.fleetType',
        validateCell: VehicleModelImportHelper.validateFleetType
    },
    {
        titleKey: 'masterRiderPersonaProperties.volumeUnit',
        id: 'volumeUnit',
        accessor: ['volume', 'unit'],
        required: true,
        templateValue:
            'masterImportTemplateValues.masterRiderPersona.volumeUnit',
        processCell: VehicleModelImportHelper.processVolumeUnitType,
        validateCell: VehicleModelImportHelper.validateVolumeUnit
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.length',
        id: 'length',
        accessor: ['dimensions', 'length'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.length',
        validateCell: VehicleModelImportHelper.validateDimensions
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.breadth',
        id: 'breadth',
        accessor: ['dimensions', 'breadth'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.breadth',
        validateCell: VehicleModelImportHelper.validateDimensions
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.height',
        id: 'height',
        accessor: ['dimensions', 'height'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.height',
        validateCell: VehicleModelImportHelper.validateDimensions
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.containerLength',
        id: 'containerLength',
        accessor: ['containerDimensions', 'length'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.containerLength'
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.containerBreadth',
        id: 'containerBreadth',
        accessor: ['containerDimensions', 'breadth'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.containerBreadth'
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.containerHeight',
        id: 'containerHeight',
        accessor: ['containerDimensions', 'height'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.containerHeight'
    },
    {
        titleKey: 'masterVehicleModelProperties.volume',
        id: 'volume',
        required: true,
        accessor: ['volume', 'value'],
        templateValue: 'masterImportTemplateValues.masterVehicleModel.volume',
        validateCell: VehicleModelImportHelper.validateVolume
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.maxPayloadWeight',
        id: 'maxPayloadWeight',
        accessor: ['maxPayloadWeight'],
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.maxPayloadWeight',
        validateCell: VehicleModelImportHelper.validateMaxPayloadWeight
    },
    {
        titleKey: 'masterVehicleModelProperties.loadingUnloadingSide',
        id: 'unloadingSide',
        required: true,
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.unloadingSide',
        processCell: VehicleModelImportHelper.processUpperCaseId,
        validateCell: VehicleModelImportHelper.validateUnloadingSide
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.doorWidth',
        id: 'doorWidth',
        templateValue: 'masterImportTemplateValues.masterVehicleModel.doorWidth'
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.doorHeight',
        id: 'doorHeight',
        templateValue:
            'masterImportTemplateValues.masterVehicleModel.doorHeight'
    },
    {
        titleKey: 'masterVehicleModelProperties.withUnit.userCount',
        id: 'userCount',
        templateValue: 'masterImportTemplateValues.masterVehicleModel.userCount'
    }
];

export default VEHICLEMODEL_IMPORT_FORMAT;
